import clsx from 'clsx';
import React, { FC, useRef, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import UploaderContent from './UploaderContent';
import UploadError from './UploadError';
import Attachment from './Attachment';
import LinearDashedBorder from './LinearDashedBorder';
import Vector from './Vector';
import { uploadInvoice } from 'api/orders/api';

export interface UploaderProps {
  type: 'dashboard' | 'order-service' | 'inventory';
  onUploadSuccess?: (uploadId: string, invoiceId: string) => void;
  onReset: () => void;
}

const Uploader: FC<UploaderProps> = ({ type, onUploadSuccess, onReset }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [errorUploadError, setUploadError] = useState<boolean | null>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
  const ALLOWED_TYPES = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'application/pdf',
    'text/plain',
  ];

  const validateFile = (file: File): boolean | null => {
    if (!ALLOWED_TYPES.includes(file.type)) return true;
    if (file.size > MAX_FILE_SIZE) return true;
    return false;
  };

  const queryClient = useQueryClient();

  const [progress, setProgress] = useState(0);
  const uploadInterval = useRef<NodeJS.Timeout | null>(null);

  const { mutate: updateResourceMutation, reset } = useMutation(uploadInvoice, {
    onSuccess: (data) => {
      if (uploadInterval.current) clearInterval(uploadInterval.current);
      setProgress(100);
      const uploadId = data?.upload_id;
      const invoiceId = data?.invoice_id;
      onUploadSuccess?.(uploadId, invoiceId);

      setFile(null);
      setProgress(0);
      setUploadError(false);
      if (inputRef.current) inputRef.current.value = '';

      queryClient.invalidateQueries(['non-granite-inventory-table']);
    },
    onError: () => {
      if (uploadInterval.current) clearInterval(uploadInterval.current);
      setUploadError(true);
      setFile(null);
      setProgress(0);
    },
    onSettled: () => {
      setUploadLoading(false);
    },
  });

  const onFileChange = (file: File) => {
    setUploadLoading(true);

    const validationError = validateFile(file);
    if (validationError) {
      setUploadError(true);
      setFile(null);
      setProgress(0);
      setUploadLoading(false);

      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setFile(file);
    setProgress(10);

    uploadInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 90) {
          const next = prev + 10;
          return next > 90 ? 90 : next;
        }
        return prev;
      });
    }, 500);

    const formData = new FormData();
    formData.append('file', file);
    updateResourceMutation(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileChange(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file) onFileChange(file);
    }
  };

  const onClear = () => {
    setUploadError(false);
    setFile(null);
    reset();
    onReset();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onRepeat = () => {
    if (file) onFileChange(file);
    else onClear();
  };

  return (
    <div
      className={clsx(
        'overflow- group relative cursor-pointer overflow-hidden bg-[linear-gradient(97.76deg,_rgba(126,_119,_179,_0.08)_3.17%,_rgba(137,_137,_255,_0.08)_26.59%,_rgba(108,_157,_255,_0.08)_49.93%,_rgba(120,_207,_218,_0.08)_73.45%,_rgba(159,_244,_169,_0.08)_96.88%)]',
        {
          'h-[254px]': type === 'dashboard',
          'h-[294px] ': type === 'order-service',
          'h-[352px] ': type === 'inventory',
        },
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      {...(!errorUploadError &&
        !file && {
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
          onClick: () => inputRef.current?.click(),
        })}
    >
      <Vector type={type} />
      <input
        type="file"
        className="hidden"
        ref={inputRef}
        onChange={handleFileChange}
        accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,application/pdf,text/plain"
      />
      <LinearDashedBorder />
      <div className="flex h-full flex-col items-center justify-center p-4">
        {!errorUploadError && !file && (
          <UploaderContent hovered={hovered} isDragOver={isDragOver} />
        )}
        {errorUploadError && !uploadLoading && (
          <UploadError onClear={onClear} />
        )}
        {file && progress !== 0 && !errorUploadError && (
          <Attachment
            file={file}
            onCancel={onClear}
            percentage={progress}
            status={progress < 100 ? 'Processing' : 'Success'}
            onRepeat={onRepeat}
          />
        )}
      </div>
    </div>
  );
};

export default Uploader;
